import { api } from "./client";
import type { Interview, InterviewStatus, TurnRole } from "@mycompbox/shared";

export type { Interview, InterviewStatus };

/**
 * Interview API client (ENG-144 web deeper-interview flow).
 *
 * An Interview is a LIFECYCLE + correlation record for the web epic-request
 * interview (ENG-140/141). It links to the driving Job (`jobId`) and, once the
 * job completes, to the created EpicRequest (`epicRequestId`).
 *
 * 🔴 Architectural invariant (do not violate): this client is for interview
 * LIFECYCLE only — create/associate, read transcript, list history, reopen.
 * User ANSWERS are NOT written here; they flow through the existing job mailbox
 * (`POST /jobs/:id/turns`, see {@link ./mailbox}). There is intentionally no
 * turn-write method on this client. While an interview is `in_progress`,
 * `GET /interviews/:id` returns the LIVE `JobTurn` rows (via the linked job);
 * once completed it returns the durable `InterviewTurn` snapshot.
 *
 * Endpoints (all under /api/v1):
 *   POST   /interviews       — start (optionally linked to a Job via jobId)
 *   GET    /interviews       — list the caller's interviews (paginated history)
 *   GET    /interviews/:id   — transcript (live JobTurn | completed snapshot)
 *   PATCH  /interviews/:id   — lifecycle transition (reopen completed → in_progress)
 */

// =============================================================================
// Types
// =============================================================================

/** A single transcript turn — from the live job log or the durable snapshot. */
export interface InterviewTranscriptTurn {
  id: string;
  role: TurnRole;
  content: string;
  /** ISO timestamp (JSON-serialized). */
  createdAt: string;
}

/**
 * A transcript view (GET /interviews/:id): the interview plus its turns, tagged
 * with the source (`live` while in_progress, `snapshot` once completed).
 */
export interface InterviewTranscript {
  interview: Interview;
  source: "live" | "snapshot";
  turns: InterviewTranscriptTurn[];
}

/**
 * Body for POST /interviews. `requestedById`/`organizationId` are derived
 * server-side. `jobId` links the interview to its driving job so the completion
 * hook (ENG-142) can stamp/snapshot it — the web flow ALWAYS passes it at start.
 */
export interface CreateInterviewInput {
  jobId?: string;
  epicRequestId?: string;
  teamId?: string;
}

/** Query params for GET /interviews. */
export interface ListInterviewsParams {
  cursor?: string;
  limit?: number;
  status?: InterviewStatus;
}

/** Cursor-paginated interview history (GET /interviews). */
export interface PaginatedInterviews {
  data: Interview[];
  meta: {
    cursor: string | null;
    hasMore: boolean;
    totalCount: number;
  };
}

// =============================================================================
// API Functions
// =============================================================================

export const interviewsApi = {
  /** Start an interview, optionally linked to a job. Returns the created record. */
  create: (input: CreateInterviewInput = {}) =>
    api.post<{ data: Interview }>("/interviews", input).then((res) => res.data),

  /** Fetch an interview transcript (live turns while in_progress). */
  get: (id: string) =>
    api
      .get<{ data: InterviewTranscript }>(`/interviews/${id}`)
      .then((res) => res.data),

  /** List the caller's interviews (history), newest first. */
  list: (params: ListInterviewsParams = {}) => {
    const query = new URLSearchParams();
    if (params.cursor) query.set("cursor", params.cursor);
    if (params.limit !== undefined) query.set("limit", String(params.limit));
    if (params.status) query.set("status", params.status);
    const qs = query.toString();
    return api.get<PaginatedInterviews>(`/interviews${qs ? `?${qs}` : ""}`);
  },

  /** Reopen a completed interview (completed → in_progress) so it can be enriched. */
  reopen: (id: string) =>
    api
      .patch<{ data: Interview }>(`/interviews/${id}`, { action: "reopen" })
      .then((res) => res.data),

  /**
   * Archive (discard) an interview (in_progress | completed → archived).
   * Primary use: clearing a stuck in-progress interview whose driving job
   * failed, so it stops appearing in resume detection.
   */
  archive: (id: string) =>
    api
      .patch<{ data: Interview }>(`/interviews/${id}`, { action: "archive" })
      .then((res) => res.data),

  /**
   * Continue a completed interview whose linked ER is still pending (ENG-148):
   * dispatches a NEW seeded job (jobB), copies the prior transcript into it, and
   * re-points the interview at jobB. Returns the reopened interview plus the new
   * `jobId` to navigate to. Rejects (server 4xx) if the interview isn't
   * `completed` or the linked ER isn't `pending`.
   */
  continue: (id: string) =>
    api
      .post<{ data: { interview: Interview; jobId: string } }>(
        `/interviews/${id}/continue`,
        {}
      )
      .then((res) => res.data),
};
